import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Image } from '../components/apartment/header/header.component';
import gsap from 'gsap';

@Injectable({
    providedIn: 'root',
})
export class DOMHandlerService {
    isMobile = window.innerWidth < 768;
    isDesktop = window.innerWidth >= 1024;
    intlDateFormatGerman = new Intl.DateTimeFormat('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    intlDateFormatEnglish = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    constructor(private sanitizer: DomSanitizer) {}

    toSafeHTML(html: string) {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    getSmallestImage(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        if (image.formats?.thumbnail) {
            return image.formats.thumbnail.url;
        }
        if (image.formats?.small) {
            return image.formats.small.url;
        }
        if (image.formats?.medium) {
            return image.formats.medium.url;
        }
        if (image.formats?.large) {
            return image.formats.large.url;
        }
        return image.url;
    }

    getSmallestImageAsWebp(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        if (image.formats?.thumbnail) {
            return image.formats.thumbnail.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.small) {
            return image.formats.small.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.medium) {
            return image.formats.medium.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.large) {
            return image.formats.large.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        return image.url.replace('upload', 'upload/t_to-webp');
    }

    getLargestImage(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        if (image.formats?.large) {
            return image.formats.large.url;
        }
        if (image.formats?.medium) {
            return image.formats.medium.url;
        }
        if (image.formats?.small) {
            return image.formats.small.url;
        }
        if (image.formats?.thumbnail) {
            return image.formats.thumbnail.url;
        }
        return image.url;
    }

    getLargestImageAsWebp(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        if (image.formats?.large) {
            return image.formats.large.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.medium) {
            return image.formats.medium.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.small) {
            return image.formats.small.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        if (image.formats?.thumbnail) {
            return image.formats.thumbnail.url.replace(
                'upload',
                'upload/t_to-webp',
            );
        }
        return image.url.replace('upload', 'upload/t_to-webp');
    }

    generateSrcset(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        let srcset = '';
        if (image.formats?.large) {
            srcset = `${image.formats.large.url} ${image.formats.large.width}w, `;
        }
        if (image.formats?.medium) {
            srcset += `${image.formats.medium.url} ${image.formats.medium.width}w, `;
        }
        if (image.formats?.small) {
            srcset += `${image.formats.small.url} ${image.formats.small.width}w, `;
        }
        if (image.formats?.thumbnail) {
            srcset += `${image.formats.thumbnail.url} ${image.formats.thumbnail.width}w, `;
        }
        return srcset;
    }
    generateSrcsetAsWebp(image: Image | undefined): string {
        if (!image) {
            return '';
        }
        let srcset = '';
        if (image.formats?.large) {
            srcset = `${image.formats.large.url.replace('upload', 'upload/t_to-webp')} ${image.formats.large.width}w, `;
        }
        if (image.formats?.medium) {
            srcset += `${image.formats.medium.url.replace('upload', 'upload/t_to-webp')} ${image.formats.medium.width}w, `;
        }
        if (image.formats?.small) {
            srcset += `${image.formats.small.url.replace('upload', 'upload/t_to-webp')} ${image.formats.small.width}w, `;
        }
        if (image.formats?.thumbnail) {
            srcset += `${image.formats.thumbnail.url.replace('upload', 'upload/t_to-webp')} ${image.formats.thumbnail.width}w, `;
        }
        return srcset;
    }

    spawnAddToCartAnimation(event: MouseEvent | undefined, amount: number) {
        if (!event) {
            return;
        }
        const mousePosition = this.getMousePosition(event);
        const animatedDiv = document.createElement('div');
        animatedDiv.classList.add('addToCartItem');
        animatedDiv.style.left = `${mousePosition.x}px`;
        animatedDiv.style.top = `${mousePosition.y}px`;
        animatedDiv.innerHTML = `+${amount}`;
        document.body.appendChild(animatedDiv);

        const targetElement = this.isDesktop
            ? document.getElementById('cart-item-amount-desktop')
            : document.getElementById('cart-item-amount-mobile');

        if (targetElement) {
            const targetRect = targetElement.getBoundingClientRect();

            gsap.to(animatedDiv, {
                x: targetRect.left - mousePosition.x,
                y: targetRect.top - mousePosition.y,
                scale: 0.5,
                opacity: 0,
                duration: 1,
                ease: 'power3.in',
                onComplete: () => {
                    document.body.removeChild(animatedDiv);
                },
            });
        } else {
            setTimeout(() => {
                document.body.removeChild(animatedDiv);
            }, 1000);
        }
    }

    formatDate(value: string, languageCode: string): string {
        if (languageCode === 'en') {
            return this.intlDateFormatEnglish.format(new Date(value));
        }
        return this.intlDateFormatGerman.format(new Date(value));
    }

    closeCollapse(id: string) {
        const element = document.getElementById(id);
        if (!element) {
            return;
        }
        //@ts-ignore
        window?.HSCollapse?.hide(element);
    }

    addStructuredData(data: any) {
        const existingScript = document.querySelector(
            'script[type="application/ld+json"]',
        );
        if (existingScript) {
            document.head.removeChild(existingScript);
        }

        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
    }

    getApartmentStructuredData() {
        return {
            '@context': 'https://schema.org',
            '@type': 'VacationRental',
            additionalType: 'Apartment',
            name: 'Apartment Zehentmoar',
            description:
                'Gemütliches Wohngefühl in Kombination mit Natur und Tradition in der Region! Unsere Familie empfängt seit den 90er-Jahren Gäste aus Nah und Fern.',
            image: [
                'https://res.cloudinary.com/dmowrqef3/image/upload/v1736070111/DSC_00147_9b4660d5f7.webp',
                'https://res.cloudinary.com/dmowrqef3/image/upload/v1736070112/DSC_00146_5877014462.webp',
                'https://res.cloudinary.com/dmowrqef3/image/upload/v1736073508/DSC_00171_212a0e96f7.webp',
                'https://res.cloudinary.com/dmowrqef3/image/upload/v1736707065/DSC_00144_d85f4d137e.webp',
                'https://res.cloudinary.com/dmowrqef3/image/upload/v1736070111/DSC_00158_caced7ac80.webp',
            ],
            identifier: 'ZEHENTMOAR-APARTMENT-123',
            url: 'https://www.zehentmoar.at/de/apartment/buchen',
            telephone: '+4366475027592',
            email: 'apartment@zehentmoar.at',
            address: {
                '@type': 'PostalAddress',
                streetAddress: 'Winkler Straße 88',
                addressLocality: 'Gröbming',
                addressRegion: 'Steiermark',
                postalCode: '8962',
                addressCountry: 'AT',
            },
            geo: {
                '@type': 'GeoCoordinates',
                latitude: '47.44820928144097',
                longitude: '13.875608590520958',
            },
            checkinTime: '15:30:00+01:00',
            checkoutTime: '10:00:00+01:00',
            petsAllowed: false,
            containsPlace: {
                '@type': 'Accommodation',
                additionalType: 'EntirePlace',
                occupancy: {
                    '@type': 'QuantitativeValue',
                    value: 6,
                },
                bed: [
                    {
                        '@type': 'BedDetails',
                        numberOfBeds: 1,
                        typeOfBed: 'Queen',
                    },
                    {
                        '@type': 'BedDetails',
                        numberOfBeds: 2,
                        typeOfBed: 'Single',
                    },
                ],
                amenityFeature: [
                    {
                        '@type': 'LocationFeatureSpecification',
                        name: 'kitchen',
                        value: true,
                    },
                    {
                        '@type': 'LocationFeatureSpecification',
                        name: 'wifi',
                        value: true,
                    },
                    {
                        '@type': 'LocationFeatureSpecification',
                        name: 'balcony',
                        value: true,
                    },
                    {
                        '@type': 'LocationFeatureSpecification',
                        name: 'parkingType',
                        value: 'Free',
                    },
                    {
                        '@type': 'LocationFeatureSpecification',
                        name: 'childFriendly',
                        value: true,
                    },
                ],
                floorSize: {
                    '@type': 'QuantitativeValue',
                    value: 90,
                    unitCode: 'MTK',
                },
                numberOfBathroomsTotal: 2,
                numberOfBedrooms: 3,
                numberOfRooms: 5,
            },
            knowsLanguage: ['de', 'en'],
            paymentAccepted: ['Cash'],
            currenciesAccepted: 'EUR',
            openingHoursSpecification: {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: [
                    'Monday',
                    'Tuesday',
                    'Wednesday',
                    'Thursday',
                    'Friday',
                    'Saturday',
                    'Sunday',
                ],
                opens: '00:00',
                closes: '23:59',
            },
        };
    }

    private getMousePosition(event: MouseEvent) {
        return {
            x: event.clientX,
            y: event.clientY,
        };
    }
}
